(function () {
  const KEY = 'discountBuyRecoveryV2';
  function createRecovery(fetchCart, storage, root = '/') {
    let running = null;
    const read = () => {
      const state = JSON.parse(storage.getItem(KEY) || 'null');
      if (state && (state.version !== 2 || !Array.isArray(state.items) || !['checkout', 'clearing', 'restoring', 'failed'].includes(state.phase))) throw new Error('Saved cart data could not be read.');
      if (state) state.items.forEach(normalize);
      return state;
    };
    const write = (state) => storage.setItem(KEY, JSON.stringify(state));
    const properties = (value) => Object.fromEntries(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
    const normalize = (item) => {
      const id = String(item.variant_id || item.id), quantity = Number(item.quantity);
      if (!/^\d+$/.test(id) || !Number.isSafeInteger(quantity) || quantity <= 0) throw new Error('Saved cart item could not be read.');
      return { id, quantity, properties: properties(item.properties), selling_plan: item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null };
    };
    const identity = (item) => { const n = normalize(item); return JSON.stringify([n.id, n.properties, String(n.selling_plan || '')]); };
    const totals = (items) => { const result = new Map(); for (const item of items) { const n = normalize(item), key = identity(n); result.set(key, (result.get(key) || 0) + n.quantity); } return result; };
    const equal = (a, b) => { const left = totals(a), right = totals(b); return left.size === right.size && [...left].every(([key, quantity]) => right.get(key) === quantity); };
    async function request(route, body) {
      const response = await fetchCart(root + route, { method: body === undefined ? 'GET' : 'POST', headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) }, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
      if (!response.ok) { const error = new Error(`Cart request failed (${response.status}).`); error.status = response.status; throw error; }
      return response.json();
    }
    async function cart() { const value = await request('cart.js'); if (!Array.isArray(value.items)) throw new Error('Unable to read cart.'); return value; }
    async function mutate(route, body, confirmed) {
      let failure;
      try { await request(route, body); } catch (error) { failure = error; }
      // A lost response can still mean that Shopify applied the mutation. Always read before retrying.
      const actual = await cart();
      if (!confirmed(actual)) throw failure || new Error('Cart update could not be verified.');
      return actual;
    }
    async function prepare(detail) {
      if (read()) throw new Error('Restore your previous cart before starting another bundle checkout.');
      const current = await cart();
      const state = { version: 2, phase: 'checkout', items: current.items.map(normalize), detail };
      write(state); // Storage failure must stop checkout before the cart permalink replaces it.
      return state;
    }
    function recover(manual = false) {
      if (running) return running;
      const state = read();
      if (!state || (state.phase === 'failed' && !manual)) return Promise.resolve(false);
      running = (async () => {
        try {
          let current = await cart();
          if (!equal(current.items, state.items)) {
            if (state.phase === 'checkout') {
              state.phase = 'clearing'; write(state);
            }
            if (state.phase === 'clearing' || state.resume === 'clearing') {
              current = await mutate('cart/clear.js', {}, (value) => value.items.length === 0);
              state.phase = 'restoring'; delete state.resume; write(state);
            } else { state.phase = 'restoring'; delete state.resume; write(state); }
            // Reconcile from actual line keys on every retry; never blindly repeat an add.
            const wanted = totals(state.items);
            for (let count = 0; !equal(current.items, state.items); count++) {
              if (count > state.items.length * 4 + current.items.length * 4 + 20) throw new Error('Cart restoration needs another retry.');
              const actual = totals(current.items);
              const excess = current.items.find((item) => (actual.get(identity(item)) || 0) > (wanted.get(identity(item)) || 0));
              if (excess) {
                const key = identity(excess), before = actual.get(key), quantity = Math.max(0, Number(excess.quantity) - (before - (wanted.get(key) || 0)));
                current = await mutate('cart/change.js', { id: excess.key, quantity }, (value) => (totals(value.items).get(key) || 0) === before - Number(excess.quantity) + quantity);
                continue;
              }
              const missing = state.items.find((item) => (actual.get(identity(item)) || 0) < wanted.get(identity(item)));
              if (!missing) throw new Error('Cart contents could not be matched.');
              const key = identity(missing), quantity = wanted.get(key) - (actual.get(key) || 0);
              current = await mutate('cart/add.js', { id: missing.id, quantity, properties: missing.properties, ...(missing.selling_plan ? { selling_plan: missing.selling_plan } : {}) }, (value) => (totals(value.items).get(key) || 0) === wanted.get(key));
            }
          }
          const verified = await cart();
          if (!equal(verified.items, state.items)) throw new Error('Cart verification failed.');
          storage.removeItem(KEY);
          for (const key of ['discountBuyActive', 'discountBuyRestoreCartItems', 'discountBuyStickyQty', 'discountBuyStickyVariantId']) storage.removeItem(key);
          return { detail: state.detail || {}, cart: verified };
        } catch (error) {
          state.resume = state.phase === 'failed' ? state.resume : state.phase;
          state.phase = 'failed';
          write(state);
          throw error;
        } finally { running = null; }
      })();
      return running;
    }
    function migrateLegacy() {
      if (read() || storage.getItem('discountBuyActive') !== 'true') return;
      const items = JSON.parse(storage.getItem('discountBuyRestoreCartItems') || 'null');
      if (!Array.isArray(items)) throw new Error('Previous cart snapshot is unavailable.');
      write({ version: 2, phase: 'checkout', items: items.map(normalize), detail: { quantity: Number(storage.getItem('discountBuyStickyQty')) || 1, variantId: storage.getItem('discountBuyStickyVariantId') || '' } });
    }
    return { prepare, recover, read, migrateLegacy };
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = { createRecovery, KEY }; return; }
  if (window.__discountButtonsIsolatedBuyLoaded) return;
  window.__discountButtonsIsolatedBuyLoaded = true;
  const recovery = createRecovery(window.fetch.bind(window), window.sessionStorage, window.Shopify?.routes?.root || '/');
  let busy = false, departing = false, retryAction;
  async function refreshCartUI(cart) {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update', context: 'cart',
      lines: cart.items.map((item) => ({ id: item.key, quantity: item.quantity })),
      promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, itemCount: cart.item_count, source: 'discount-cart-recovery', didError: false } }),
    }));
  }
  function message(text, retry) {
    document.querySelectorAll('[data-discount-recovery-status]').forEach((node) => { node.hidden = !text; node.querySelector('[data-discount-recovery-message]').textContent = text; node.querySelector('button').hidden = !retry; });
    retryAction = retry;
  }
  async function restore(manual = false) {
    if (busy || departing) return;
    busy = true;
    try {
      recovery.migrateLegacy();
      if (recovery.read()?.phase === 'failed' && !manual) { message('Your previous cart has not been fully restored. Please retry before starting another bundle checkout.', () => restore(true)); return; }
      const restored = await recovery.recover(manual);
      if (restored) {
        document.dispatchEvent(new CustomEvent('sticky-cart:force-quantity', { detail: restored.detail }));
        const refresh = async () => {
          try { await refreshCartUI(restored.cart); message('Your previous cart has been restored.'); }
          catch { message('Your cart is restored, but its display could not refresh. Retry the display update or reload this page.', refresh); }
        };
        await refresh(); // Display failures cannot run recovery again or re-add items.
      }
    } catch (error) {
      const cause = error.status === 422 ? 'Some saved items may no longer be available.' : error.status === 429 ? 'Cart requests are temporarily limited. Please wait before retrying.' : 'We could not finish restoring your previous cart.';
      message(`${cause} Your current cart may be partially restored. Saved items are kept; Retry attempts to restore the previous cart.`, () => restore(true));
    }
    finally { busy = false; }
  }
  async function buy(button) {
    if (busy || departing) return;
    busy = true;
    try {
      if (recovery.read()) { message('Please restore your previous cart before starting another bundle checkout.', () => restore(true)); return; }
      message('Preparing bundle checkout...');
      const input = document.querySelector('sticky-add-to-cart [ref="quantityInput"]');
      await recovery.prepare({ quantity: Math.max(1, parseInt(input?.value || '1', 10) || 1), variantId: document.querySelector('sticky-add-to-cart')?.dataset.currentVariantId || '' });
      departing = true;
      window.location.href = button.getAttribute('href');
    } catch (error) { message('We could not safely save your current cart. Checkout has not started. Please retry.', () => buy(button)); }
    finally { busy = false; }
  }
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const retry = event.target.closest('[data-discount-recovery-retry]');
    if (retry) { event.preventDefault(); retryAction?.(); return; }
    const button = event.target.closest('[data-direct-buy-option="true"]');
    if (!button || !button.getAttribute('href') || button.getAttribute('href') === '#') return;
    event.preventDefault(); buy(button);
  }, true);
  window.addEventListener('pagehide', () => { departing = true; });
  window.addEventListener('pageshow', () => { departing = false; restore(); });
  window.addEventListener('focus', () => restore());
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') restore(); });
  restore();
})();
